import React, { useState, useEffect } from 'react';
import { useCanceledJobs } from '../hooks/useScheduleJobs';
import { useSchedulePermissions } from '../hooks/useSchedulePermissions';
import { useServiceTypes } from '../hooks/useServiceTypes';
import PendingJobsColumn from '../components/schedule/layout/PendingJobsColumn';
import ScheduleHeader from '../components/schedule/layout/ScheduleHeader';
import EditJobModal from '../components/EditJobModal';
import AddJobModal from '../components/AddJobModal';
import { getFormattedDateRange } from '../utils/timezone-utils';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function CanceledJobs() {
  // Get user from auth context, handle the case where it might be undefined
  const { user, loading: authLoading } = useAuth() || {};
  const navigate = useNavigate();
  
  // State for filters
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Default to single day view (today) - users can scroll or change date range to see more
  const [endDate, setEndDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const [copyJobData, setCopyJobData] = useState(null);
  
  // Add state to track selected property ID for proper filtering
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  
  // Enhanced service type filtering with role-based auto-initialization
  const {
    availableServiceTypes,
    selectedServiceTypes,
    setSelectedServiceTypes,
    isRoleFilterEnabled,
    setRoleFilterEnabled,
    loading: serviceTypesLoading,
    filterInfo
  } = useServiceTypes({
    enableRoleFilter: !['ADMIN', 'GUEST_APP_MANAGER'].includes(user?.role), // Enable role filter by default for non-admin users
    autoSelectFiltered: true // Auto-select role-appropriate service types
  });
  
  // Initialize selectedAreas with user's assigned areas when user is loaded
  useEffect(() => {
    // Don't initialize while auth is still loading
    if (authLoading) {
      return;
    }
    
    if (user?.areas && Array.isArray(user.areas) && user.areas.length > 0) {
      // Only set if selectedAreas is currently empty to avoid overriding user selections
      setSelectedAreas(prevSelectedAreas => {
        if (prevSelectedAreas.length === 0) {
          return user.areas;
        }
        return prevSelectedAreas;
      });
    }
  }, [user, authLoading]); // Only depend on user and authLoading to avoid re-running unnecessarily
  
  // Filter handlers
  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };
  
  const handleServiceTypeChange = (serviceTypes) => {
    setSelectedServiceTypes(serviceTypes);
  };
  
  const handleAreaChange = (areas) => {
    setSelectedAreas(areas);
  };
  
  const resetFilters = () => {
    setSelectedDate(new Date());
    // Reset to single day view (today) - users can scroll or change date range to see more
    setEndDate(new Date());
    setSearchTerm('');
    setSelectedCategories([]);
    
    // Reset service types to role-appropriate defaults if role filter is enabled
    if (isRoleFilterEnabled) {
      // The hook will handle auto-selecting role-appropriate types
      // We just need to trigger a reset by clearing first, then the hook will auto-select
      setSelectedServiceTypes([]);
      // The useEffect in the hook will auto-select role-appropriate types
    } else {
      setSelectedServiceTypes([]);
    }
    
    // Clear all areas instead of reverting to user areas
    // This allows users to see all areas after reset
    setSelectedAreas([]);
    
    // Clear selected property ID
    setSelectedPropertyId(null);
    
    // Reset the jobs data to trigger date range reset in the hook
    refetchAllJobs();
  };
  
  // Check if the user has permission to add jobs
  const canAddJobs = user && ['ADMIN', 'GUEST_APP_MANAGER', 'HOUSEKEEPER_MANAGER', 'PROPERTY_MANAGER'].includes(user.role);
  
  // Check if the user has restricted view (only sees assigned jobs)
  const hasRestrictedView = user && ['INSPECTOR', 'MAINTENANCE', 'HOUSEKEEPER'].includes(user.role);
  
  // Get all the hooks we need
  const {
    pendingJobsByDate,
    jobsLoading,
    isLoadingMore,
    hasMore,
    handleLoadMore,
    totalJobs,
    displayedJobsCount,
    jobsByDateTotals,
    refetchAllJobs,
    userHasSelectedDateRange,
    currentDateRangeDescription
  } = useCanceledJobs(
    selectedDate,
    endDate,
    searchTerm,
    selectedCategories,
    selectedServiceTypes,
    selectedAreas,
    user,
    selectedPropertyId,
    '' // teamMemberSearchTerm - using empty string since this page uses general searchTerm
  );

  // Debug logging
  useEffect(() => {
    console.log('CanceledJobs Debug:', {
      userRole: user?.role,
      selectedDate: selectedDate?.toISOString(),
      endDate: endDate?.toISOString(),
      totalJobs,
      displayedJobsCount,
      jobsLoading,
      pendingJobsByDate: Object.keys(pendingJobsByDate || {}).length,
      canAccessCanceledJobs: ['ADMIN', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER'].includes(user?.role)
    });
  }, [user?.role, selectedDate, endDate, totalJobs, displayedJobsCount, jobsLoading, pendingJobsByDate]);
  
  const {
    selectedJobs,
    onJobSelect,
    onJobEdit,
    canEditJobs,
    editingJob,
    closeEditModal
  } = useSchedulePermissions();
  
  // Handler for job updates via modal
  const handleJobUpdate = (forceNetworkRefresh = false) => {
    // Force network refresh for reservation updates to ensure computed fields recalculate
    // Call refetchAllJobs which will clear stale state and fetch fresh data
    refetchAllJobs(forceNetworkRefresh);
  };
  
  // Handler for adding a new job
  const handleAddNewJob = (newJob) => {
    refetchAllJobs();
    setIsAddJobModalOpen(false);
    setCopyJobData(null); // Clear copy data after successful creation
  };

  // Handler for copying a job
  const handleCopyJob = (copyData) => {
    setCopyJobData(copyData);
    setIsAddJobModalOpen(true);
  };
  
  // Create the Add Job button to pass as rightContent
  const addJobButton = canAddJobs ? (
    <button
      onClick={() => setIsAddJobModalOpen(true)}
      className="ml-4 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center justify-center gap-2"
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      Add Job
    </button>
  ) : null;

  
  return (
    <div className="mx-auto pb-6 sm:px-6 lg:px-8">
      {/* Message for users with restricted view */}
      {hasRestrictedView && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Note:</span> As a {user?.role?.toLowerCase()}, you are only seeing canceled jobs assigned to you.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Role-based service type filter notification */}
      {filterInfo?.isFiltered && filterInfo?.hasRoleBasedRestriction && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <span className="font-medium">Smart Filter Active:</span> Showing service types for your role ({filterInfo.totalAvailable} of {filterInfo.totalInSystem} types). You can toggle this in the service type filter.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Schedule Header with filters */}
      <ScheduleHeader
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        endDate={endDate}
        setEndDate={setEndDate}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategories={selectedCategories}
        handleCategoryToggle={handleCategoryToggle}
        selectedServiceTypes={selectedServiceTypes}
        onServiceTypeChange={handleServiceTypeChange}
        availableServiceTypes={availableServiceTypes}
        isRoleFilterEnabled={isRoleFilterEnabled}
        onRoleFilterToggle={setRoleFilterEnabled}
        serviceTypesLoading={serviceTypesLoading}
        serviceTypeFilterInfo={filterInfo}
        selectedAreas={selectedAreas}
        onAreaChange={handleAreaChange}
        resetFilters={resetFilters}
        userRole={user?.role}
        rightContent={addJobButton}
        title="Canceled Jobs"
        selectedPropertyId={selectedPropertyId}
        setSelectedPropertyId={setSelectedPropertyId}
      />
      
      {/* Main Jobs Section - Shows all canceled jobs */}
      <div className="mt-2">
        {!jobsLoading && totalJobs === 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <span className="font-medium">No canceled jobs found</span> for the selected date range ({currentDateRangeDescription}). 
                  Try adjusting the date range or filters to see more results.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <PendingJobsColumn
          pendingJobsByDate={pendingJobsByDate}
          jobsLoading={jobsLoading}
          isLoadingMore={isLoadingMore}
          selectedJobs={selectedJobs}
          onJobSelect={onJobSelect}
          onJobEdit={onJobEdit}
          canEditJobs={canEditJobs}
          allJobs={Object.values(pendingJobsByDate).flat()}
          hasMore={hasMore}
          handleLoadMore={handleLoadMore}
          totalJobs={totalJobs}
          displayedJobsCount={displayedJobsCount}
          jobsByDateTotals={jobsByDateTotals}
          searchTerm={searchTerm}
          selectedAreas={selectedAreas}
          selectedServiceTypes={selectedServiceTypes}
          selectedCategories={selectedCategories}
          userHasSelectedDateRange={userHasSelectedDateRange}
          currentDateRangeDescription={currentDateRangeDescription}
        />
      </div>

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal 
          job={editingJob} 
          onClose={closeEditModal} 
          onUpdate={handleJobUpdate} 
          onCopyJob={handleCopyJob}
        />
      )}
      
      {/* Add Job Modal */}
      {isAddJobModalOpen && (
        <AddJobModal
          isOpen={isAddJobModalOpen}
          onClose={() => {
            setIsAddJobModalOpen(false);
            setCopyJobData(null); // Clear copy data when modal closes
          }}
          onCreateJob={handleAddNewJob}
          initialPropertyId={copyJobData?.propertyId}
          initialProperty={copyJobData?.property}
          initialNotes={copyJobData?.notes}
          initialScheduledDate={copyJobData?.scheduledDate}
          initialPayout={copyJobData?.payout}
          initialBilledAmount={copyJobData?.billedAmount}
        />
      )}

      {/* Back to Schedule Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={() => navigate('/dashboard/schedule')}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-lg"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Schedule
        </button>
      </div>
    </div>
  );
} 